/**
 * next-42 §5 — page_view 의 UTM 기록(순수 함수). user_events.properties(jsonb) 에만 싣는다 — 열 추가 없음.
 *   · utm_source / utm_medium / utm_campaign / utm_content : 현재 URL 쿼리에 «있을 때만»
 *   · utm_first : 세션 첫 랜딩의 utm 묶음(sessionStorage 'kd_utm_first')을 이후 page_view 에도 실는다
 */
export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'] as const;
export type UtmKey = (typeof UTM_KEYS)[number];
export type UtmSet = Partial<Record<UtmKey, string>>;
export const UTM_FIRST_STORAGE_KEY = 'kd_utm_first';
const MAX_LEN = 100;

/** 쿼리 문자열(「?a=b」 또는 「a=b」) → utm 4종 중 있는 것만. 값은 trim·100자 절단, 빈 값 제외. */
export function extractUtm(search: string | null | undefined): UtmSet {
  const out: UtmSet = {};
  const q = String(search ?? '').replace(/^\?/, '');
  if (!q) return out;
  let sp: URLSearchParams;
  try { sp = new URLSearchParams(q); } catch { return out; }
  for (const k of UTM_KEYS) {
    const v = (sp.get(k) ?? '').trim();
    if (v) out[k] = v.slice(0, MAX_LEN);
  }
  return out;
}

export interface UtmStore { getItem(k: string): string | null; setItem(k: string, v: string): void }

/**
 * page_view properties 에 덧붙일 utm 묶음.
 *   현재 URL 의 utm 이 있으면 그대로 싣고, 보존소에 첫 랜딩이 없으면 그 utm 을 첫 랜딩으로 저장한다.
 *   보존소에 첫 랜딩이 있으면 utm_first 로 싣는다(현재 URL 이 첫 랜딩 자신이어도 저장 직후에는 싣지 않는다 — 중복 방지).
 *   utm 이 하나도 없는 첫 랜딩은 저장하지 않는다(유기 유입은 utm_first 없음).
 */
export function utmProperties(search: string | null | undefined, store: UtmStore | null): Record<string, unknown> {
  const cur = extractUtm(search);
  const props: Record<string, unknown> = { ...cur };
  if (!store) return props;
  let first: UtmSet | null = null;
  try {
    const raw = store.getItem(UTM_FIRST_STORAGE_KEY);
    if (raw) { const p = JSON.parse(raw); if (p && typeof p === 'object') first = extractUtm(new URLSearchParams(p as Record<string, string>).toString()); }
  } catch { first = null; }
  if (first && Object.keys(first).length) { props.utm_first = first; return props; }
  if (Object.keys(cur).length) { try { store.setItem(UTM_FIRST_STORAGE_KEY, JSON.stringify(cur)); } catch { /* 보존 실패는 무시 */ } }
  return props;
}
